import React, { Fragment, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import { Tabs as MaterialTabs, Tab as MaterialTab } from '@material-ui/core';
import { LoadingSpinner } from 'legacy-packages/ft-cb';

import './ScoringRubrics.scss';

const ScoringRubrics = ({
  question,
  onTabChange,
  rubricIndex,
  isLoading,
  tabsActionCallback,
  guidelinesHelp,
  isMobile,
  params,
  isScoringItem,
}) => {
  const rubrics = question.children;

  if (!rubrics) {
    return null;
  }

  function handleChange(event, newValue) {
    onTabChange(newValue);
  }

  const hasTabs = rubrics;
  const considerScrolling = hasTabs && rubrics.length > 2;
  const selectedTabRef = useRef(null);

  /*
    Use useLayoutEffect instead of useEffect to ensure that the scrollIntoView operation
    happens synchronously after the DOM mutations but before the browser paints the changes to the screen.
    This helps prevent flickering.
  */
  useLayoutEffect(() => {
    if (
      selectedTabRef.current &&
      isScoringItem &&
      question.id === params?.questionId
    ) {
      selectedTabRef.current.scrollIntoView({ block: 'center' });
    }
  }, [params?.questionId]);

  return (
    <Fragment>
      <div className="title-container">
        {guidelinesHelp ? (
          <Fragment>
            <h2 className="title" data-test-rubrics-headline>
              {guidelinesHelp.title}
            </h2>
            <p>{guidelinesHelp.content}</p>
          </Fragment>
        ) : (
          !isMobile && (
            <h2 className="title" data-test-rubrics-headline>
              Scoring Guidelines
            </h2>
          )
        )}
      </div>

      {hasTabs && (
        <MaterialTabs
          action={tabsActionCallback}
          variant={considerScrolling ? 'scrollable' : 'standard'}
          value={rubricIndex}
          onChange={handleChange}
          textColor="primary"
          scrollButtons="auto"
          className="scoring-rubrics-tabs"
        >
          {rubrics.map((rubric, index) => (
            <MaterialTab
              data-test-rubric-tab
              key={rubric.learnosityItemReference}
              label={rubric.title}
              ref={index === rubricIndex ? selectedTabRef : null}
            />
          ))}
        </MaterialTabs>
      )}

      {isLoading && (
        <div className="loading-spinner">
          <LoadingSpinner />
        </div>
      )}

      {/* The block below is rendering with visibility=hidden because it needs to be in the dom for the math tags to be
      properly processed, but it also need to have its size preserved in order to avoid performance degradation.
      See: https://github.com/mathjax/MathJax/issues/1179#issuecomment-97897405
      */}
      <div className={cn('scroll', { '--invisible': isLoading })}>
        {rubrics.map((rubric, index) => (
          <div
            data-test-rubric-body={index.toString()}
            hidden={index !== rubricIndex}
            key={rubric.learnosityItemReference}
            className="content-item"
            tabIndex="0"
            role="tabpanel"
          >
            <div
              className="learnosity-item"
              data-reference={rubric.learnosityItemReference}
            />
          </div>
        ))}
      </div>
    </Fragment>
  );
};

ScoringRubrics.defaultProps = {
  onTabChange: () => {},
  tabsActionCallback: () => {},

  rubricIndex: 0,

  question: {
    children: [],
  },

  hasNextQuestion: true,
  hasPreviousQuestion: true,
};

export default ScoringRubrics;
